import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Building2, Sparkles, ChefHat, Upload, X, Plus, Save, Trash2, Loader2, Link as LinkIcon, ExternalLink } from "lucide-react";

const UNIDADES = [
  { slug: "petit", label: "Celebra Petit", icon: Building2, color: "emerald" },
  { slug: "kermesita", label: "La Kermesita", icon: Sparkles, color: "pink" },
  { slug: "bocatto", label: "Bocatto", icon: ChefHat, color: "orange" },
];

const UnidadesPublicas = () => {
  const [unidad, setUnidad] = useState("petit");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/unidades-publicas/${unidad}`);
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  }, [unidad]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/unidades-publicas/${unidad}`, data);
      toast.success("Información actualizada · los clientes ya la ven");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const subirFoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await axios.post(`${API}/admin/unidades-publicas/${unidad}/fotos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData({ ...data, fotos: r.fotos });
      toast.success("Foto subida");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const eliminarFoto = async (url) => {
    if (!window.confirm("¿Eliminar esta foto de la galería?")) return;
    try {
      const { data: r } = await axios.delete(`${API}/admin/unidades-publicas/${unidad}/fotos`, { data: { url } });
      setData({ ...data, fotos: r.fotos });
      toast.success("Foto eliminada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const addItem = (campo) => setData({ ...data, [campo]: [...(data[campo] || []), ""] });
  const updItem = (campo, i, v) => setData({ ...data, [campo]: data[campo].map((x, j) => j === i ? v : x) });
  const delItem = (campo, i) => setData({ ...data, [campo]: data[campo].filter((_, j) => j !== i) });

  if (!data) return <div className="p-6 text-stone-500">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="admin-unidades-publicas">
      <div>
        <h1 className="font-display text-3xl text-stone-900">Configuración pública de unidades</h1>
        <p className="text-stone-500 text-sm">Edita lo que tus clientes ven en la página de reservas (texto, fotos, qué incluye, políticas).</p>
      </div>

      {/* Selector de unidad */}
      <div className="grid grid-cols-3 gap-2">
        {UNIDADES.map(u => {
          const Icon = u.icon;
          const activo = unidad === u.slug;
          return (
            <button key={u.slug} onClick={() => setUnidad(u.slug)} className={`p-3 rounded-lg border-2 ${activo ? `border-${u.color}-500 bg-${u.color}-50` : "border-stone-200 bg-white hover:border-stone-400"}`} data-testid={`unidad-tab-${u.slug}`}>
              <Icon className="w-5 h-5 mx-auto mb-1" />
              <p className="text-sm font-medium">{u.label}</p>
              <p className="text-[10px] text-stone-500 mt-1">/reservar/{u.slug}</p>
            </button>
          );
        })}
      </div>

      {loading ? <p className="text-stone-500">Cargando...</p> : (
        <Card><CardContent className="p-5 space-y-5">
          {/* Datos base */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nombre comercial</Label>
              <Input value={data.nombre || ""} onChange={(e) => setData({ ...data, nombre: e.target.value })} data-testid="inp-nombre" />
            </div>
            <div className="md:col-span-2">
              <Label>Descripción</Label>
              <Textarea rows={3} value={data.descripcion || ""} onChange={(e) => setData({ ...data, descripcion: e.target.value })} data-testid="inp-desc" />
            </div>
            <div>
              <Label>Capacidad</Label>
              <Input value={data.capacidad || ""} onChange={(e) => setData({ ...data, capacidad: e.target.value })} data-testid="inp-cap" />
            </div>
            <div>
              <Label>Ubicación</Label>
              <Input value={data.ubicacion || ""} onChange={(e) => setData({ ...data, ubicacion: e.target.value })} data-testid="inp-ubi" />
            </div>
            <div>
              <Label>Precio base</Label>
              <Input type="number" value={data.precio_base || 0} onChange={(e) => setData({ ...data, precio_base: Number(e.target.value) || 0 })} data-testid="inp-precio" />
            </div>
            <div>
              <Label>Moneda / unidad</Label>
              <Input value={data.moneda || "MXN"} onChange={(e) => setData({ ...data, moneda: e.target.value })} placeholder="MXN o MXN/persona" data-testid="inp-moneda" />
            </div>
          </div>

          {/* Galería */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Galería de fotos</Label>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => subirFoto(e.target.files?.[0])} data-testid="inp-foto" />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-700 text-white rounded text-sm hover:bg-emerald-800">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Subir foto
                </span>
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(data.fotos || []).map((url, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden border bg-stone-100 group">
                  <img src={url.startsWith("http") ? url : `${API.replace("/api", "")}${url}`} alt={`foto-${i}`} className="w-full h-full object-cover" />
                  <button onClick={() => eliminarFoto(url)} className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition" data-testid={`del-foto-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(!data.fotos || data.fotos.length === 0) && <p className="col-span-full text-stone-400 text-xs text-center py-4">Sin fotos. Sube la primera con el botón verde.</p>}
            </div>
          </div>

          {/* Incluye */}
          <ListEditor title="¿Qué incluye?" items={data.incluye || []} onAdd={() => addItem("incluye")} onUpd={(i, v) => updItem("incluye", i, v)} onDel={(i) => delItem("incluye", i)} tid="incluye" />

          {/* Políticas */}
          <ListEditor title="Políticas" items={data.politicas || []} onAdd={() => addItem("politicas")} onUpd={(i, v) => updItem("politicas", i, v)} onDel={(i) => delItem("politicas", i)} tid="politicas" />

          {/* Actions */}
          <div className="flex items-center justify-between border-t pt-4">
            <a href={`/reservar/${unidad}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline" data-testid="link-public">
              <ExternalLink className="w-3 h-3" /> Ver página pública
            </a>
            <Button onClick={guardar} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-guardar-unidad">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Guardar cambios
            </Button>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

const ListEditor = ({ title, items, onAdd, onUpd, onDel, tid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Label>{title}</Label>
      <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${tid}`}><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
    </div>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input value={it} onChange={(e) => onUpd(i, e.target.value)} data-testid={`${tid}-${i}`} />
          <Button size="sm" variant="ghost" onClick={() => onDel(i)} data-testid={`del-${tid}-${i}`}><X className="w-3 h-3 text-red-600" /></Button>
        </div>
      ))}
    </div>
  </div>
);

export default UnidadesPublicas;
